export const INDUSTRY_CATEGORY_OPTIONS = Object.freeze([
  "Hair / Barbering",
  "Nails",
  "Lashes",
  "Brows",
  "Makeup",
  "Skincare",
  "Massage",
  "Waxing / Hair Removal",
  "Tattoo / Permanent Makeup",
  "Other",
]);

export const OTHER_INDUSTRY_CATEGORY = "Other";

const INDUSTRY_CATEGORY_LOOKUP = new Map(
  INDUSTRY_CATEGORY_OPTIONS.map((option) => [option.toLowerCase(), option]),
);

const LEGACY_INDUSTRY_CATEGORY_ALIASES = new Map([
  ["hair (stylist, barber, colorist)", "Hair / Barbering"],
  ["barbering", "Hair / Barbering"],
  ["nails (nail tech)", "Nails"],
  ["lashes (lash tech)", "Lashes"],
  ["brows (brow artist)", "Brows"],
  ["makeup (mua)", "Makeup"],
  ["skincare (esthetician)", "Skincare"],
  ["massage therapy", "Massage"],
]);

const LEGACY_INDUSTRY_KEYWORD_MAPPINGS = [
  {
    category: "Tattoo / Permanent Makeup",
    regex: /\b(tattoo|permanent makeup|pmu|microblading)\b/i,
  },
  {
    category: "Waxing / Hair Removal",
    regex: /\b(wax|sugar(ing)?|epilat|hair removal)\b/i,
  },
  {
    category: "Massage",
    regex: /\bmassage|massage\s+therap/i,
  },
  {
    category: "Skincare",
    regex: /\b(esthetic|skincare|skin care|facial)\b/i,
  },
  {
    category: "Makeup",
    regex: /\b(makeup|mua)\b/i,
  },
  {
    category: "Brows",
    regex: /\b(brow|eyebrow)\b/i,
  },
  {
    category: "Lashes",
    regex: /\b(lash|eyelash)\b/i,
  },
  {
    category: "Nails",
    regex: /\b(nail|manicure|pedicure)\b/i,
  },
  {
    category: "Hair / Barbering",
    regex: /\b(barber|fade|clipper)\b/i,
  },
  {
    category: "Hair / Barbering",
    regex: /\b(hair|stylist|colorist|salon)\b/i,
  },
];

const toTrimmedString = (value) => {
  if (typeof value === "string") return value.trim();
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

export const normalizeIndustryCategory = (value) => {
  const trimmed = toTrimmedString(value);
  if (!trimmed) return "";
  const lookupKey = trimmed.toLowerCase();
  return (
    INDUSTRY_CATEGORY_LOOKUP.get(lookupKey) ||
    LEGACY_INDUSTRY_CATEGORY_ALIASES.get(lookupKey) ||
    ""
  );
};

export const mapLegacyIndustryToCategory = (value) => {
  const legacyIndustry = toTrimmedString(value);
  if (!legacyIndustry) {
    return {
      industryCategory: "",
      industryCustom: "",
    };
  }

  const normalizedCategory = normalizeIndustryCategory(legacyIndustry);
  if (normalizedCategory) {
    return {
      industryCategory: normalizedCategory,
      industryCustom: "",
    };
  }

  const mappedCategory = LEGACY_INDUSTRY_KEYWORD_MAPPINGS.find(({ regex }) =>
    regex.test(legacyIndustry),
  )?.category;
  if (mappedCategory) {
    return {
      industryCategory: mappedCategory,
      industryCustom: "",
    };
  }

  return {
    industryCategory: OTHER_INDUSTRY_CATEGORY,
    industryCustom: legacyIndustry,
  };
};

export const getDisplayIndustry = (professional = {}) => {
  const normalizedCategory = normalizeIndustryCategory(professional?.industryCategory);
  const industryCustom = toTrimmedString(professional?.industryCustom);
  const legacyIndustry = toTrimmedString(professional?.industry);

  if (normalizedCategory && normalizedCategory !== OTHER_INDUSTRY_CATEGORY) {
    return normalizedCategory;
  }

  if (normalizedCategory === OTHER_INDUSTRY_CATEGORY && industryCustom) {
    return industryCustom;
  }

  if (legacyIndustry) {
    return legacyIndustry;
  }

  if (industryCustom) {
    return industryCustom;
  }

  return "";
};

export const normalizeIndustryRecord = (professional = {}) => {
  const legacyIndustry = toTrimmedString(professional?.industry);
  let industryCategory = normalizeIndustryCategory(professional?.industryCategory);
  let industryCustom = toTrimmedString(professional?.industryCustom);

  if (!industryCategory && legacyIndustry) {
    const mapped = mapLegacyIndustryToCategory(legacyIndustry);
    industryCategory = mapped.industryCategory;
    if (industryCategory === OTHER_INDUSTRY_CATEGORY) {
      industryCustom = mapped.industryCustom;
    }
  }

  if (industryCategory !== OTHER_INDUSTRY_CATEGORY) {
    industryCustom = "";
  }

  const normalizedLegacyIndustry =
    industryCategory === OTHER_INDUSTRY_CATEGORY
      ? industryCustom
      : industryCategory || legacyIndustry;

  return {
    industry: normalizedLegacyIndustry,
    industryCategory,
    industryCustom,
    displayIndustry: getDisplayIndustry({
      industryCategory,
      industryCustom,
      industry: normalizedLegacyIndustry || legacyIndustry,
    }),
  };
};
